import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as SuccessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { telephonyApi, personsApi } from '../../services/apiClient';

const TRANSPORT_OPTIONS = [
  { value: 'tls', label: 'TLS (Recommended)' },
  { value: 'udp', label: 'UDP (Fallback)' },
];

/**
 * Provision Person SIP Dialog
 * Creates a SIP session (extension + credentials) for an existing person.
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {Function} onSuccess - Called after successful provisioning
 */
export default function ProvisionPersonDialog({ open, onClose, onSuccess }) {
  const [phase, setPhase] = useState('form'); // 'form' | 'loading' | 'success' | 'error'
  const [transport, setTransport] = useState('tls');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  // Person picker state
  const [persons, setPersons] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [loadingPersons, setLoadingPersons] = useState(false);

  // Fetch persons list
  const fetchPersons = useCallback(async () => {
    setLoadingPersons(true);
    try {
      const { data } = await personsApi.getAll();
      const list = data.persons || data.data || data || [];
      setPersons(Array.isArray(list) ? list : []);
    } catch {
      setPersons([]);
    } finally {
      setLoadingPersons(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchPersons();
    }
  }, [open, fetchPersons]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setPhase('form');
      setTransport('tls');
      setResult(null);
      setError('');
      setCopied('');
      setSelectedPerson(null);
    }
  }, [open]);

  const handleProvision = async () => {
    if (!selectedPerson) return;
    setPhase('loading');
    setError('');
    try {
      const { data } = await telephonyApi.provisionPersonSipDevice({
        personId: selectedPerson.id,
        transport,
      });
      setResult(data.data);
      setPhase('success');
      onSuccess?.();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to provision SIP credentials for person'
      );
      setPhase('error');
    }
  };

  const handleCopy = (value, key) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleCopyAll = () => {
    if (!result) return;
    const text = [
      `Person: ${result.person.friendlyName}`,
      '',
      '--- SIP Configuration ---',
      `Server: ${result.sip.server}`,
      `Port: ${result.sip.port}`,
      `Transport: ${result.sip.transport.toUpperCase()}`,
      `Extension: ${result.sip.extension}`,
      `SIP Password: ${result.sip.password}`,
      `Realm: ${result.sip.realm}`,
      `WSS URL: ${result.sip.wssUrl}`,
    ].join('\n');
    navigator.clipboard.writeText(text);
    setCopied('all');
    setTimeout(() => setCopied(''), 2000);
  };

  const personOptions = useMemo(() => {
    return persons.map((p) => ({
      id: p.id,
      name: p.friendlyName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || `Person #${p.id}`,
      location: p.location?.friendlyName || '',
    }));
  }, [persons]);

  // ─── Credential Row Helper ────────────────────────────────────
  const CredRow = ({ label, value, keyName, mono = true }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 1.5,
        '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
      }}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 130, fontSize: '0.82rem' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontFamily: mono ? 'monospace' : 'inherit', fontSize: '0.85rem' }}
        >
          {value}
        </Typography>
        <Tooltip title={copied === keyName ? 'Copied!' : 'Copy'}>
          <IconButton size="small" onClick={() => handleCopy(value, keyName)}>
            {copied === keyName ? (
              <SuccessIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <CopyIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        Provision Person SIP Device
      </DialogTitle>

      <DialogContent>
        {/* ─── Form Phase ─── */}
        {phase === 'form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              Create SIP credentials for an existing person. This will assign a SIP extension
              so they can make and receive calls via the Resident App.
            </Alert>

            <Autocomplete
              size="small"
              options={personOptions}
              loading={loadingPersons}
              value={selectedPerson ? personOptions.find((p) => p.id === selectedPerson.id) || null : null}
              onChange={(_, v) => setSelectedPerson(v ? { id: v.id, friendlyName: v.name } : null)}
              getOptionLabel={(o) => o.name}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.location && (
                      <Typography variant="caption" color="text.secondary">{option.location}</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Select Person" placeholder="Search persons..." required />
              )}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Transport</InputLabel>
              <Select value={transport} label="Transport" onChange={(e) => setTransport(e.target.value)}>
                {TRANSPORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* ─── Loading Phase ─── */}
        {phase === 'loading' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress />
            <Typography color="text.secondary">
              Creating SIP credentials for person...
            </Typography>
          </Box>
        )}

        {/* ─── Error Phase ─── */}
        {phase === 'error' && (
          <Box sx={{ pt: 1 }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" onClick={() => setPhase('form')}>Try Again</Button>
          </Box>
        )}

        {/* ─── Success Phase — Credentials ─── */}
        {phase === 'success' && result && (
          <Box sx={{ pt: 1 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              SIP credentials provisioned for <strong>{result.person.friendlyName}</strong>
            </Alert>

            <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
              Save these credentials now. The SIP password is shown only once.
            </Alert>

            {/* SIP Configuration */}
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              SIP Configuration
            </Typography>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
              <CredRow label="Server" value={result.sip.server} keyName="server" />
              <CredRow label="Port" value={String(result.sip.port)} keyName="port" />
              <CredRow label="Transport" value={result.sip.transport.toUpperCase()} keyName="transport" />
              <CredRow label="Extension" value={result.sip.extension} keyName="ext" />
              <CredRow label="SIP Password" value={result.sip.password} keyName="sipPwd" />
              <CredRow label="Realm" value={result.sip.realm} keyName="realm" />
              <CredRow label="WSS URL" value={result.sip.wssUrl} keyName="wss" />
            </Box>

            <Button
              fullWidth
              variant="outlined"
              startIcon={copied === 'all' ? <SuccessIcon color="success" /> : <CopyIcon />}
              onClick={handleCopyAll}
            >
              {copied === 'all' ? 'Copied!' : 'Copy All Credentials'}
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {phase === 'success' ? 'Close' : 'Cancel'}
        </Button>
        {phase === 'form' && (
          <Button
            variant="contained"
            onClick={handleProvision}
            disabled={!selectedPerson}
          >
            Provision
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
